import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomerLayout from './CustomerLayout';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../supabase/supabase';
import { emailService } from '../../services/emailService';
import { Button, cn } from '../../components/ui/Button';
import Toast from '../../components/ui/Toast';
import { useToast } from '../../hooks/useToast';

export default function CustomerBookings() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelModal, setCancelModal] = useState({ isOpen: false, bookingId: null });
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  
  const { toast, showToast, hideToast } = useToast();

  const fetchBookings = async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    
    if (user) {
      const { data, error } = await supabase
        .from('bookings_1740423033529')
        .select(`
          *,
          customer:customer_id(full_name, email, phone),
          barber:barber_id(full_name, email),
          service:service_id(name)
        `)
        .eq('customer_id', user.id)
        .order('created_at', { ascending: false });
        
      if (!error) setBookings(data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const executeCancel = async () => {
    if (!cancelReason.trim()) {
      showToast("Please provide a cancellation reason.", 'error');
      return;
    }
    
    setCancelling(true);
    const id = cancelModal.bookingId;

    const { error } = await supabase
      .from('bookings_1740423033529')
      .update({ 
        status: 'cancelled',
        cancellation_reason: cancelReason.trim()
      })
      .eq('id', id);

    if (!error) {
      setBookings(prev => prev.map(b => b.id === id ? { ...b, status: 'cancelled' } : b));
      
      const booking = bookings.find(b => b.id === id);
      const details = {
        customerName: booking.customer?.full_name,
        customerPhone: booking.customer?.phone,
        barberName: booking.barber?.full_name,
        serviceName: booking.service?.name,
        date: booking.booking_date,
        time: booking.booking_time,
        reason: cancelReason.trim()
      };

      if (booking.customer?.email) {
        emailService.sendBookingCancelled({
          ...details, 
          manageLink: `${window.location.origin}/booking/c/${id}`
        }, booking.customer.email);
      }
      if (booking.barber?.email) {
        emailService.sendBookingCancelled({
          ...details, 
          manageLink: `${window.location.origin}/booking/b/${id}`
        }, booking.barber.email);
      }

      setCancelModal({ isOpen: false, bookingId: null });
      setCancelReason('');
      showToast('Booking cancelled successfully.', 'success');
    } else {
      showToast("Error cancelling booking.", 'error');
    }
    setCancelling(false);
  };

  return (
    <CustomerLayout>
      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
      <div className="p-6 relative">
        <h1 className="text-2xl font-black text-brand-dark mb-6 tracking-tight">My Bookings</h1>
        
        {loading ? (
          <div className="text-center py-10 opacity-50 font-bold uppercase text-[10px] tracking-widest">Loading history...</div>
        ) : bookings.length === 0 ? (
          <div className="bg-white rounded-[2rem] p-10 text-center border border-dashed border-gray-200">
            <SafeIcon name="Calendar" className="text-gray-300 text-3xl mx-auto mb-4" />
            <p className="text-sm font-bold text-gray-500">You haven't made any bookings yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking) => (
              <div key={booking.id} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-black text-lg text-brand-dark">{booking.barber?.full_name || 'Removed Barber'}</h3>
                    <p className="text-sm text-gray-500 font-bold mt-1">{booking.service?.name}</p>
                  </div>
                  <span className={cn(
                    "px-3 py-1 rounded-full text-[10px] font-black uppercase border",
                    booking.status === 'confirmed' ? 'bg-blue-50 text-blue-600 border-blue-100' :
                    booking.status === 'completed' ? 'bg-green-50 text-green-600 border-green-100' :
                    booking.status === 'cancelled' ? 'bg-red-50 text-red-600 border-red-100' :
                    'bg-gray-50 text-gray-600 border-gray-200'
                  )}>
                    {booking.status}
                  </span>
                </div>
                
                <div className="flex items-center gap-4 text-sm text-gray-600 border-t border-gray-50 pt-4">
                  <div className="flex items-center gap-1 font-medium">
                    <SafeIcon name="Calendar" className="text-xs text-brand-primary" />
                    {booking.booking_date}
                  </div>
                  <div className="flex items-center gap-1 font-medium">
                    <SafeIcon name="Clock" className="text-xs text-brand-primary" />
                    {booking.booking_time}
                  </div>
                  <div className="ml-auto font-black text-brand-dark text-lg">Rs. {Number(booking.total_price).toLocaleString()}</div>
                </div>

                {(booking.status === 'pending' || booking.status === 'confirmed') && (
                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <button 
                      onClick={() => navigate(`/booking/c/${booking.id}`)}
                      className="py-2.5 rounded-xl bg-brand-primary/10 text-brand-primary text-xs font-black uppercase tracking-widest active:scale-95 transition-transform"
                    >
                      Manage Live
                    </button>
                    <button 
                      onClick={() => setCancelModal({ isOpen: true, bookingId: booking.id })}
                      className="py-2.5 rounded-xl bg-red-50 text-red-600 text-xs font-black uppercase tracking-widest active:scale-95 transition-transform"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Cancel Modal */}
        {cancelModal.isOpen && (
          <div className="fixed inset-0 bg-brand-dark/60 z-50 flex items-center justify-center p-6 backdrop-blur-sm">
            <div className="bg-white rounded-[2rem] p-8 max-w-sm w-full shadow-2xl animate-in zoom-in-95">
              <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon name="XCircle" className="text-3xl" />
              </div>
              <h2 className="text-xl font-black text-brand-dark mb-2 text-center">Cancel Appointment</h2>
              <p className="text-gray-500 font-medium text-sm mb-6 text-center leading-relaxed">
                Please let us know why you are cancelling. This helps barbers manage their schedule.
              </p>
              
              <textarea 
                placeholder="Reason for cancellation (required)..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl mb-6 text-sm font-medium outline-none focus:border-red-500 min-h-[100px]"
              />

              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => { setCancelModal({ isOpen: false, bookingId: null }); setCancelReason(''); }}
                >
                  Keep Booking
                </Button>
                <Button 
                  variant="danger" 
                  className="flex-1"
                  disabled={!cancelReason.trim() || cancelling}
                  onClick={executeCancel}
                >
                  {cancelling ? 'Cancelling...' : 'Confirm'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </CustomerLayout>
  );
}